import { useMemo, useState, type FormEvent, type JSX, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  Inline,
  Stack,
  StatePill,
  TextInput,
  type StatePillTone,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";

import { formatTimestampSafe } from "../../../locale-format.js";
import { GtccCapHint } from "../../GtccCapHint.js";
import { ControlNote } from "../control/ControlPrimitives.js";

import { ApiError, apiGet } from "../../../lib/api.js";
import {
  PASSWORD_MIN_LENGTH,
  changeOwnEmail,
  changeOwnPassword,
  classifyMeFailure,
  validateEmailInput,
  validatePasswordInput,
  type EmailChangeInput,
  type EmailChangeResult,
  type MeApiFailure,
  type PasswordChangeInput,
} from "../../../profile/meProfileMutations.js";
import { useSession } from "../../../session/SessionProvider.js";

/**
 * B-GTCC-A25 — register row 49 ("Eigenes Profil") as a read-only page.
 *
 * The row names five things: identity, roles, platform grants, MFA state and
 * the caller's own sessions. Three of them can be read today, and that
 * measurement is what shapes this page rather than a guess about what a
 * profile page usually shows:
 *
 *   - Identity, roles, tenant and session expiry come out of the answer the
 *     console already holds, `GET /v1/auth/session`
 *     (`services/api/src/auth/routes.ts:487`), via `useSession()`. gtcc1's own
 *     profile page does exactly the same and has no read query of its own
 *     (`apps/admin/src/profile/ProfilePage.tsx:23`).
 *   - Platform grants have a list route
 *     (`services/api/src/modules/admin/platform-grants.routes.ts:113`), but it
 *     is owner-only (:128) and lists grants tenant-wide, not "mine". Grant
 *     activity is readable anyway, the same way `GtccSecurityView.tsx` already
 *     reads it tenant-wide: `GET /v1/admin/audit` entries carry a `targetType`,
 *     and `platform_access_grant` is one of its values. The caller's own audit
 *     query below already fetches every entry for the caller's `actor_sub`,
 *     so the grants card partitions that same response by `targetType`
 *     rather than issuing a second request — this can only show grants the
 *     caller acted on (issued or revoked), not grants received, since the
 *     audit endpoint has no `target_id` filter to key off the caller as a
 *     target instead of an actor.
 *   - MFA has no state to read, because MFA does not exist yet: the three
 *     endpoints in `services/api/src/auth/mfa.routes.ts` (:54, :81, :111) are
 *     writes, each answering 503 while `MFA_ENABLED` is false — and false is
 *     the default everywhere (`services/api/src/env.ts:522`).
 *   - Sessions: `GET /v1/admin/sessions`
 *     (`services/api/src/modules/admin/sessions.routes.ts:75`) lists the
 *     tenant's sessions, newest first, revoked and expired included, and has
 *     no `sub` filter — so this page (C-GTCC-PROF1) reads one page of
 *     `SESSION_LIMIT` rows and keeps only the rows whose `sub` is the
 *     caller's. The route sits behind `tenant.members.write`
 *     (`sessions.service.ts:210`); a caller without that permission gets a
 *     403, which the card states as such instead of as a generic failure.
 *     Beside the list stays the caller's own security events from
 *     `GET /v1/admin/audit`
 *     (`services/api/src/modules/admin/audit.routes.ts:36`), narrowed with the
 *     `actor_sub` filter that route accepts (`audit.routes.ts:70`).
 *
 * The missing subject (MFA) keeps a card instead of disappearing: row 49
 * names it, and a sentence saying why nothing can be read is a measurement a
 * reader can check. An empty card would only look like an empty result.
 *
 * E-GTCC-49-WRITE1 — the writing half of row 49: two cards change the
 * caller's own password and e-mail through the two routes gtcc1 already
 * uses, `POST /v1/me/password` and `POST /v1/me/email`
 * (`services/api/src/modules/identity/me.routes.ts`, session-scoped via
 * `requireTenant()`, no owner gate). Wire shapes, client checks and the
 * server's error codes live in `apps/admin/src/profile/meProfileMutations.ts`,
 * shared with gtcc1's `ProfilePage.tsx` so the two consoles cannot drift.
 * What differs is only the rendering: the server's code is attached to the
 * field it is about (`invalid_current_password` → current password,
 * `policy_violation` / `invalid_body` → new password, `same_email` /
 * `email_in_use` → address), a 401 or 403 is stated as a session or
 * permission hint with the request id, and there is no generic "something
 * went wrong" line — an unknown answer names its status. The e-mail card
 * shows the server's `{ email, verified }` answer and, while `verified` is
 * false, says that the address still awaits confirmation.
 *
 * Every other request the view makes is a GET; the two POSTs go out only
 * on a submitted form, never on render.
 *
 * A-GTCC-18 wires this into `registry.ts` under the `security` group as
 * `prof`.
 */

interface AuditEntry {
  readonly id: string;
  readonly tenantId: string;
  readonly actorSub: string;
  readonly actorEmail: string;
  readonly action: string;
  readonly targetType: string;
  readonly targetId: string;
  readonly createdAt: string;
}

interface AuditListResponse {
  readonly items: readonly AuditEntry[];
  readonly nextCursor: string | null;
}

/** Wire shape of one `GET /v1/admin/sessions` row (`sessions.service.ts`). */
interface SessionListItem {
  readonly id: string;
  readonly sub: string;
  readonly createdAt: string;
  readonly lastSeenAt: string;
  readonly expiresAt: string;
  readonly revokedAt: string | null;
}

interface SessionListResponse {
  readonly items: readonly SessionListItem[];
  readonly total: number;
}

const EVENT_LIMIT = 20;

/**
 * One page of the tenant list is the most this view can ask for in one GET:
 * `DEFAULT_SESSION_PAGE_SIZE` in `sessions.service.ts:92`. The route has no
 * `sub` filter, so the caller's rows are picked out client-side.
 */
const SESSION_LIMIT = 100;

type OwnSessionState = "revoked" | "expired" | "active";

function ownSessionState(item: SessionListItem, now: number): OwnSessionState {
  if (item.revokedAt !== null) return "revoked";
  const at = Date.parse(item.expiresAt);
  if (Number.isFinite(at) && at <= now) return "expired";
  return "active";
}

const OWN_SESSION_TONES: Readonly<Record<OwnSessionState, StatePillTone>> = {
  revoked: "danger",
  expired: "neutral",
  active: "success",
};

function isForbidden(error: Error | null): boolean {
  return error instanceof ApiError && error.status === 403;
}

/** Where a failed profile write lands on the card: on one field, or as a hint above the form. */
type WriteFailure =
  | { readonly kind: "field"; readonly field: string; readonly text: string }
  | { readonly kind: "hint"; readonly text: string; readonly requestId: string | undefined };

type Translate = (key: string, vars?: Record<string, string | number>) => string;

/**
 * Turns the failure of a profile write into a field error or a hint. The
 * codes are the ones `me.routes.ts` answers with; `fieldOfCode` says which
 * field a code belongs to and which catalog line names it. 401 without a
 * field code is the session, 403 is
 * permission, 502 is Keycloak, anything else is named by its status — no
 * generic line.
 */
function placeFailure(
  err: unknown,
  fieldOfCode: Readonly<Record<string, FieldTarget>>,
  t: Translate,
): WriteFailure {
  const failure: MeApiFailure | null = classifyMeFailure(err);
  if (!failure) {
    return { kind: "hint", text: t("admin.gtcc.prof.write.error.network"), requestId: undefined };
  }
  const target = failure.code ? fieldOfCode[failure.code] : undefined;
  if (target) {
    return { kind: "field", field: target.field, text: t(target.key) };
  }
  const requestId = failure.requestId;
  if (failure.status === 401) {
    return { kind: "hint", text: t("admin.gtcc.prof.write.error.session_expired"), requestId };
  }
  if (failure.status === 403) {
    return { kind: "hint", text: t("admin.gtcc.prof.write.error.forbidden"), requestId };
  }
  if (failure.code === "keycloak_unreachable") {
    return { kind: "hint", text: t("admin.gtcc.prof.write.error.upstream"), requestId };
  }
  return {
    kind: "hint",
    text: t("admin.gtcc.prof.write.error.status", { status: failure.status }),
    requestId,
  };
}

/** Which field a server code is about, and the catalog line that names it. */
type FieldTarget = { readonly field: string; readonly key: string };
const PASSWORD_FIELD_OF_CODE: Readonly<Record<string, FieldTarget>> = {
  invalid_current_password: {
    field: "current",
    key: "admin.gtcc.prof.password.error.invalid_current_password",
  },
  policy_violation: { field: "new", key: "admin.gtcc.prof.password.error.policy_violation" },
  invalid_body: { field: "new", key: "admin.gtcc.prof.password.error.invalid_body" },
};

const EMAIL_FIELD_OF_CODE: Readonly<Record<string, FieldTarget>> = {
  same_email: { field: "new", key: "admin.gtcc.prof.email.error.same_email" },
  email_in_use: { field: "new", key: "admin.gtcc.prof.email.error.email_in_use" },
  invalid_body: { field: "new", key: "admin.gtcc.prof.email.error.invalid_body" },
};

/** The hint line above a form: the sentence plus, when the server sent one, the request id. */
function WriteHint({
  failure,
  testId,
}: {
  readonly failure: Extract<WriteFailure, { kind: "hint" }>;
  readonly testId: string;
}): JSX.Element {
  const { t } = useLocale();
  return (
    <p role="alert" data-testid={testId} style={{ margin: 0 }}>
      {failure.text}
      {failure.requestId ? (
        <span style={{ color: "var(--gt-sys-text-muted)" }}>
          {" "}
          {t("admin.gtcc.prof.write.request_id", { requestId: failure.requestId })}
        </span>
      ) : null}
    </p>
  );
}

/**
 * Passwort aendern (row 49, write half). Same client checks as gtcc1
 * (`validatePasswordInput`: minimum length, confirmation match), same body
 * to `POST /v1/me/password`; the server's verdict lands on the field it is
 * about. Success clears the three fields.
 */
function PasswordChangeCard(): JSX.Element {
  const { t } = useLocale();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [failure, setFailure] = useState<WriteFailure | null>(null);
  const [done, setDone] = useState(false);

  const change = useMutation<void, Error, PasswordChangeInput>({
    mutationFn: (input) => changeOwnPassword(input),
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setFailure(null);
      setDone(true);
    },
    onError: (err) => {
      setFailure(placeFailure(err, PASSWORD_FIELD_OF_CODE, t));
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFailure(null);
    setDone(false);
    const clientError = validatePasswordInput(newPassword, confirmPassword);
    if (clientError === "me.profile.password.error.too_short") {
      setFailure({
        kind: "field",
        field: "new",
        text: t("admin.gtcc.prof.password.error.too_short", { min: PASSWORD_MIN_LENGTH }),
      });
      return;
    }
    if (clientError === "me.profile.password.error.mismatch") {
      setFailure({
        kind: "field",
        field: "confirm",
        text: t("admin.gtcc.prof.password.error.mismatch"),
      });
      return;
    }
    change.mutate({ currentPassword, newPassword });
  }

  const fieldError = (field: string): { error: string } | Record<never, never> =>
    failure?.kind === "field" && failure.field === field ? { error: failure.text } : {};

  return (
    <SectionCard
      testId="gtcc-prof-password"
      headingId="gtcc-prof-password-heading"
      title={t("admin.gtcc.prof.password.title")}
    >
      <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
        {t("admin.gtcc.prof.password.subtitle", { min: PASSWORD_MIN_LENGTH })}
      </p>
      <form onSubmit={onSubmit} data-testid="gtcc-prof-password-form">
        <Stack gap={2}>
          {failure?.kind === "hint" ? (
            <WriteHint failure={failure} testId="gtcc-prof-password-hint" />
          ) : null}
          <TextInput
            label={t("admin.gtcc.prof.password.current")}
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.currentTarget.value)}
            maxLength={256}
            {...fieldError("current")}
            data-testid="gtcc-prof-password-current"
          />
          <TextInput
            label={t("admin.gtcc.prof.password.new")}
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.currentTarget.value)}
            maxLength={256}
            {...fieldError("new")}
            data-testid="gtcc-prof-password-new"
          />
          <TextInput
            label={t("admin.gtcc.prof.password.confirm")}
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.currentTarget.value)}
            maxLength={256}
            {...fieldError("confirm")}
            data-testid="gtcc-prof-password-confirm"
          />
          {done ? (
            <p role="status" data-testid="gtcc-prof-password-success" style={{ margin: 0 }}>
              {t("admin.gtcc.prof.password.success")}
            </p>
          ) : null}
          <Inline gap={2}>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={change.isPending}
              data-testid="gtcc-prof-password-submit"
            >
              {change.isPending
                ? t("admin.gtcc.prof.password.submitting")
                : t("admin.gtcc.prof.password.submit")}
            </Button>
          </Inline>
        </Stack>
      </form>
    </SectionCard>
  );
}

/**
 * E-Mail aendern (row 49, write half). Same shape check as gtcc1
 * (`validateEmailInput`), same body to `POST /v1/me/email`; the server's
 * `{ email, verified }` answer is shown as such, and `verified: false` —
 * what the route answers today — is stated as "awaits confirmation" rather
 * than as done.
 */
function EmailChangeCard({ currentEmail }: { readonly currentEmail: string }): JSX.Element {
  const { t } = useLocale();
  const [newEmail, setNewEmail] = useState("");
  const [failure, setFailure] = useState<WriteFailure | null>(null);
  const [result, setResult] = useState<EmailChangeResult | null>(null);

  const change = useMutation<EmailChangeResult, Error, EmailChangeInput>({
    mutationFn: (input) => changeOwnEmail(input),
    onSuccess: (answer) => {
      setNewEmail("");
      setFailure(null);
      setResult(answer);
    },
    onError: (err) => {
      setFailure(placeFailure(err, EMAIL_FIELD_OF_CODE, t));
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFailure(null);
    setResult(null);
    const trimmed = newEmail.trim();
    if (validateEmailInput(trimmed)) {
      setFailure({ kind: "field", field: "new", text: t("admin.gtcc.prof.email.error.format") });
      return;
    }
    change.mutate({ newEmail: trimmed });
  }

  return (
    <SectionCard
      testId="gtcc-prof-email"
      headingId="gtcc-prof-email-heading"
      title={t("admin.gtcc.prof.email.title")}
    >
      <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
        {t("admin.gtcc.prof.email.subtitle", { current: currentEmail })}
      </p>
      <form onSubmit={onSubmit} data-testid="gtcc-prof-email-form">
        <Stack gap={2}>
          {failure?.kind === "hint" ? (
            <WriteHint failure={failure} testId="gtcc-prof-email-hint" />
          ) : null}
          <TextInput
            label={t("admin.gtcc.prof.email.new")}
            type="email"
            autoComplete="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.currentTarget.value)}
            maxLength={320}
            {...(failure?.kind === "field" ? { error: failure.text } : {})}
            data-testid="gtcc-prof-email-new"
          />
          {result ? (
            <Stack gap={1} data-testid="gtcc-prof-email-result">
              <p role="status" style={{ margin: 0 }}>
                {t("admin.gtcc.prof.email.success", { email: result.email })}
              </p>
              <Inline gap={2}>
                {result.verified ? (
                  <StatePill tone="success" label={t("admin.gtcc.prof.email.verified")} />
                ) : (
                  <StatePill tone="warn" label={t("admin.gtcc.prof.email.unverified")} />
                )}
              </Inline>
              {result.verified ? null : (
                <p data-testid="gtcc-prof-email-verify-hint" style={{ margin: 0 }}>
                  {t("admin.gtcc.prof.email.verify_hint", { email: result.email })}
                </p>
              )}
            </Stack>
          ) : null}
          <Inline gap={2}>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={change.isPending}
              data-testid="gtcc-prof-email-submit"
            >
              {change.isPending
                ? t("admin.gtcc.prof.email.submitting")
                : t("admin.gtcc.prof.email.submit")}
            </Button>
          </Inline>
        </Stack>
      </form>
    </SectionCard>
  );
}

function SectionCard({
  testId,
  headingId,
  title,
  children,
}: {
  readonly testId: string;
  readonly headingId: string;
  readonly title: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <section aria-labelledby={headingId} data-testid={testId}>
      <Card elevation={1}>
        <Stack gap={3}>
          <h3 id={headingId} style={{ margin: 0, fontSize: "var(--gt-sys-font-size-md)" }}>
            {title}
          </h3>
          {children}
        </Stack>
      </Card>
    </section>
  );
}

/** One labelled fact about the caller. */
function Fact({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <Stack gap={0}>
      <span style={{ color: "var(--gt-sys-text-muted)", fontSize: "var(--gt-sys-font-size-xs)" }}>
        {label}
      </span>
      <span>{children}</span>
    </Stack>
  );
}

export function GtccProfileView(): JSX.Element {
  const { t, locale } = useLocale();
  const session = useSession();

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }),
    [locale],
  );

  const ownEvents = useQuery<AuditListResponse, Error>({
    queryKey: ["gtcc", "prof", "audit", session.identity.sub],
    queryFn: ({ signal }) =>
      apiGet<AuditListResponse>(
        "/v1/admin/audit",
        { actor_sub: session.identity.sub, limit: EVENT_LIMIT },
        signal,
      ),
    staleTime: 30_000,
  });

  const ownSessions = useQuery<SessionListResponse, Error>({
    queryKey: ["gtcc", "prof", "sessions", session.identity.sub],
    queryFn: ({ signal }) =>
      apiGet<SessionListResponse>("/v1/admin/sessions", { limit: SESSION_LIMIT }, signal),
    staleTime: 30_000,
  });

  const events = ownEvents.data?.items ?? [];
  const mySessions = (ownSessions.data?.items ?? []).filter(
    (item) => item.sub === session.identity.sub,
  );
  const now = Date.now();
  const grantEvents = events.filter((entry) => entry.targetType === "platform_access_grant");

  return (
    <Stack gap={4} data-testid="gtcc-view-prof" style={{ padding: "var(--gt-sys-space-5)" }}>
      <Stack gap={1}>
        <h2 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg)" }}>
          {t("admin.gtcc.prof.title")}
        </h2>
        <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
          {t("admin.gtcc.prof.subtitle")}
        </p>
        <p style={{ margin: 0 }} data-testid="gtcc-prof-write-note">
          {t("admin.gtcc.prof.write_note")}
        </p>
      </Stack>

      <SectionCard
        testId="gtcc-prof-identity"
        headingId="gtcc-prof-identity-heading"
        title={t("admin.gtcc.prof.identity.title")}
      >
        <Inline gap={4} wrap>
          <Fact label={t("admin.gtcc.prof.identity.display_name")}>
            {session.identity.displayName ?? t("admin.gtcc.prof.identity.display_name_unset")}
          </Fact>
          <Fact label={t("admin.gtcc.prof.identity.email")}>{session.identity.email}</Fact>
          <Fact label={t("admin.gtcc.prof.identity.sub")}>
            <code>{session.identity.sub}</code>
          </Fact>
          <Fact label={t("admin.gtcc.prof.identity.tenant")}>
            <code>{session.tenantId}</code>
          </Fact>
          <Fact label={t("admin.gtcc.prof.identity.expires_at")}>
            <time dateTime={session.expiresAt}>
              {formatTimestampSafe(session.expiresAt, (d) => dateFormatter.format(d))}
            </time>
          </Fact>
        </Inline>
      </SectionCard>

      <PasswordChangeCard />

      <EmailChangeCard currentEmail={session.identity.email} />

      <SectionCard
        testId="gtcc-prof-roles"
        headingId="gtcc-prof-roles-heading"
        title={t("admin.gtcc.prof.roles.title")}
      >
        {session.roles.length === 0 ? (
          <StatePill tone="warn" label={t("admin.gtcc.prof.roles.empty")} />
        ) : (
          <Stack gap={2}>
            <Inline gap={2} wrap data-testid="gtcc-prof-roles-list">
              {session.roles.map((role) => (
                <StatePill key={role} tone="neutral" label={role} />
              ))}
            </Inline>
            <span style={{ color: "var(--gt-sys-text-muted)" }}>
              {t("admin.gtcc.prof.roles.count", { count: session.roles.length })}
            </span>
          </Stack>
        )}
      </SectionCard>

      <SectionCard
        testId="gtcc-prof-grants"
        headingId="gtcc-prof-grants-heading"
        title={t("admin.gtcc.prof.grants.title")}
      >
        <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
          {t("admin.gtcc.prof.grants.subtitle")}
        </p>
        {ownEvents.isPending ? (
          <span data-testid="gtcc-prof-grants-loading">{t("admin.gtcc.prof.grants.loading")}</span>
        ) : ownEvents.isError ? (
          <span data-testid="gtcc-prof-grants-error">{t("admin.gtcc.prof.grants.error")}</span>
        ) : grantEvents.length === 0 ? (
          <span data-testid="gtcc-prof-grants-empty">{t("admin.gtcc.prof.grants.empty")}</span>
        ) : (
          <ul
            data-testid="gtcc-prof-grants-list"
            style={{ margin: 0, paddingInlineStart: "var(--gt-sys-space-4)" }}
          >
            {grantEvents.map((entry) => (
              <li key={entry.id}>
                <Inline gap={2} wrap>
                  <time dateTime={entry.createdAt}>
                    {formatTimestampSafe(entry.createdAt, (d) => dateFormatter.format(d))}
                  </time>
                  <StatePill tone="neutral" label={entry.action} />
                </Inline>
              </li>
            ))}
          </ul>
        )}
        {ownEvents.data?.nextCursor ? (
          <ControlNote testId="gtcc-prof-grants-truncated">
            {t("admin.gtcc.audit.page_truncated")}
          </ControlNote>
        ) : null}
      </SectionCard>

      <SectionCard
        testId="gtcc-prof-mfa"
        headingId="gtcc-prof-mfa-heading"
        title={t("admin.gtcc.prof.mfa.title")}
      >
        <p style={{ margin: 0 }}>{t("admin.gtcc.prof.mfa.unavailable")}</p>
      </SectionCard>

      <SectionCard
        testId="gtcc-prof-own-sessions"
        headingId="gtcc-prof-own-sessions-heading"
        title={t("admin.gtcc.prof.own_sessions.title")}
      >
        {ownSessions.isPending ? (
          <span data-testid="gtcc-prof-own-sessions-loading">
            {t("admin.gtcc.prof.own_sessions.loading")}
          </span>
        ) : ownSessions.isError ? (
          isForbidden(ownSessions.error) ? (
            <span data-testid="gtcc-prof-own-sessions-forbidden">
              {t("admin.gtcc.prof.own_sessions.forbidden")}
            </span>
          ) : (
            <span data-testid="gtcc-prof-own-sessions-error">
              {t("admin.gtcc.prof.own_sessions.error")}
            </span>
          )
        ) : mySessions.length === 0 ? (
          <span data-testid="gtcc-prof-own-sessions-empty">
            {t("admin.gtcc.prof.own_sessions.empty")}
          </span>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              data-testid="gtcc-prof-own-sessions-list"
              aria-labelledby="gtcc-prof-own-sessions-heading"
              style={{ borderCollapse: "collapse" }}
            >
              <thead>
                <tr>
                  <th scope="col">{t("admin.gtcc.prof.own_sessions.col.created")}</th>
                  <th scope="col">{t("admin.gtcc.prof.own_sessions.col.last_seen")}</th>
                  <th scope="col">{t("admin.gtcc.prof.own_sessions.col.expires")}</th>
                  <th scope="col">{t("admin.gtcc.prof.own_sessions.col.state")}</th>
                </tr>
              </thead>
              <tbody>
                {mySessions.map((item) => {
                  const state = ownSessionState(item, now);
                  return (
                    <tr key={item.id} data-testid="gtcc-prof-own-session-row">
                      <td>
                        <time dateTime={item.createdAt}>
                          {formatTimestampSafe(item.createdAt, (d) => dateFormatter.format(d))}
                        </time>
                      </td>
                      <td>
                        <time dateTime={item.lastSeenAt}>
                          {formatTimestampSafe(item.lastSeenAt, (d) => dateFormatter.format(d))}
                        </time>
                      </td>
                      <td>
                        <time dateTime={item.expiresAt}>
                          {formatTimestampSafe(item.expiresAt, (d) => dateFormatter.format(d))}
                        </time>
                      </td>
                      <td>
                        <StatePill
                          tone={OWN_SESSION_TONES[state]}
                          label={t(`admin.gtcc.prof.own_sessions.state.${state}`)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {ownSessions.data ? (
          <GtccCapHint
            limit={SESSION_LIMIT}
            shown={ownSessions.data.items.length}
            total={ownSessions.data.total}
            testId="gtcc-prof-own-sessions-cap"
          />
        ) : null}
      </SectionCard>

      <SectionCard
        testId="gtcc-prof-sessions"
        headingId="gtcc-prof-sessions-heading"
        title={t("admin.gtcc.prof.sessions.title")}
      >
        <p style={{ margin: 0 }} data-testid="gtcc-prof-session-current">
          {t("admin.gtcc.prof.sessions.current")}
        </p>

        <Stack gap={2}>
          <span
            style={{ color: "var(--gt-sys-text-muted)", fontSize: "var(--gt-sys-font-size-xs)" }}
          >
            {t("admin.gtcc.prof.sessions.events_title")}
          </span>

          {ownEvents.isPending ? (
            <span data-testid="gtcc-prof-events-loading">
              {t("admin.gtcc.prof.sessions.loading")}
            </span>
          ) : ownEvents.isError ? (
            <span data-testid="gtcc-prof-events-error">{t("admin.gtcc.prof.sessions.error")}</span>
          ) : events.length === 0 ? (
            <span data-testid="gtcc-prof-events-empty">{t("admin.gtcc.prof.sessions.empty")}</span>
          ) : (
            <ul
              data-testid="gtcc-prof-events-list"
              style={{ margin: 0, paddingInlineStart: "var(--gt-sys-space-4)" }}
            >
              {events.map((entry) => (
                <li key={entry.id}>
                  <Inline gap={2} wrap>
                    <time dateTime={entry.createdAt}>
                      {formatTimestampSafe(entry.createdAt, (d) => dateFormatter.format(d))}
                    </time>
                    <StatePill tone="neutral" label={entry.action} />
                    <code>{entry.targetType}</code>
                  </Inline>
                </li>
              ))}
            </ul>
          )}
          {ownEvents.data?.nextCursor ? (
            <ControlNote testId="gtcc-prof-events-truncated">
              {t("admin.gtcc.audit.page_truncated")}
            </ControlNote>
          ) : null}
        </Stack>
      </SectionCard>
    </Stack>
  );
}
